import { articleMetaBySlug } from "./meta";

export const meta = articleMetaBySlug["fixed-or-adaptive-plan"]!;

export function Body() {
  return (
    <>
      <p>
        Most training plans are written in advance: twelve weeks, every session in place from
        day one. Real weeks rarely go like that. You sleep badly, work runs late, a cold
        arrives. What does research say about plans that adjust to how you are doing, compared
        with plans that stay fixed?
      </p>

      <h2>What the researchers did</h2>
      <p>
        A Finnish study followed 30 recreational runners through a 15-week training period. One
        group followed a plan fixed in advance. In the other group the plan was adjusted twice a
        week, based on heart rate variability measured during the night, how tired and sore the
        runners felt, and how their heart rate compared with their running speed. When the
        signals were good, training increased a little. When they were poor, it eased off.
      </p>
      <p>
        A large Irish study looked at something every fixed plan has to deal with: weeks that
        don't go to plan. The researchers went through more than 15 million runs from about
        300,000 recreational marathon runners and looked at breaks in their training.
      </p>

      <h2>What they found</h2>
      <p>
        Both Finnish groups got faster, but the adjusted group improved its 10K time by 6.2
        percent on average, against 2.9 percent for the fixed plan.
      </p>
      <p>
        In the Irish data, more than half of the runners had at least one short break of up to
        six days before their marathon. Breaks of a week or more were linked to finishing times
        5 to 8 percent slower. Interruptions are a normal part of training, not an exception.
      </p>

      <h2>What it means for you</h2>
      <p>
        A plan is only useful if it fits the week you actually have. If you follow a fixed
        programme, allow yourself to adjust it: make a hard session easier when you are clearly
        tired, move it when time is short, and don't squeeze missed sessions into the following
        days.
      </p>
      <p>
        After a break, start from the training you have actually done, not from where the plan
        says you should be.
      </p>

      <h2>What the studies can't tell you</h2>
      <p>
        The Finnish study was small, and the runners adjusted on several signals at once. A
        review of eight studies of training guided by heart rate variability found benefits for
        some physiological measures, but no clear overall gain in race performance. And the
        Irish study is observational: it shows how common interruptions are and how they relate
        to race times, not what the best response to them is.
      </p>

      <h2>How we use it</h2>
      <p>
        PX7 Training shows your next two or three sessions and updates them after each run,
        using what you actually completed, the time you have and how you feel. When a session
        doesn't fit the day, you get one shorter or easier alternative. Missed sessions are not
        added on top of the plan, and your goal and race date stay in view.
      </p>

      <h2>Sources</h2>
      <ul>
        <li>
          Nuuttila et al. (2022), Individualized endurance training based on recovery and
          training status in recreational runners.{" "}
          <a href="https://doi.org/10.1249/MSS.0000000000002968">Medicine & Science in Sports & Exercise</a>.
        </li>
        <li>
          Feely et al. (2023), Estimating the cost of training disruptions on marathon
          performance.{" "}
          <a href="https://doi.org/10.3389/fspor.2022.1096124">Frontiers in Sports and Active Living</a>.
        </li>
        <li>
          Düking et al. (2021), Monitoring and adapting endurance training on the basis of heart
          rate variability monitored by wearable technologies.{" "}
          <a href="https://doi.org/10.1016/j.jsams.2021.04.012">Journal of Science and Medicine in Sport</a>.
        </li>
      </ul>
    </>
  );
}
